import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  IconButton, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Snackbar,
  Alert,
  Typography, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  Select,
  Pagination,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from "@mui/material/styles";
import Loader from '../loader/loader';
import { getAllLocationGroupsListAction, deleteGrouplocationAction } from "../../redux/actions/locationGroupActions";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.common.white,
  fontFamily: '"Poppins", sans-serif',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '& td, & th': {
    padding: theme.spacing(1.5),
    fontFamily: '"Poppins", sans-serif',
  },
}));

const LocationGroup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const locationGroups = useSelector((state) => state.locationGroup.get_all_location_group_list.content || []);
  const total_count = useSelector((state) => state.locationGroup.get_all_location_group_list || {});
  const isLoading = useSelector((state) => state.globalSearchReducer.loading);
  
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  useEffect(() => {
    dispatch(getAllLocationGroupsListAction(page, rowsPerPage));
  }, [dispatch, page, rowsPerPage]);

  const handleEditClick = (group) => {
    navigate('/locationgroup', {
      state: {
        groupName: group.name, 
        selectedLocations: group.locations || [],
        status: group.status,
        isEdit: true,
        groupId: group.id
      },
    });
  };

  const handleDeleteClick = (id) => {
    setSelectedGroupId(id);
    setOpenDialog(true);
  };

  const handleConfirmDelete = () => {
    if (selectedGroupId !== null) {
      dispatch(deleteGrouplocationAction(selectedGroupId))
        .then(() => {
         
          const totalItems = total_count.totalElements || 0;
          const itemsAfterDelete = totalItems - 1;
          const maxPage = Math.max(0, Math.ceil(itemsAfterDelete / rowsPerPage) - 1);
          const newPage = page > maxPage ? maxPage : page;
          
          if (newPage !== page) {
            setPage(newPage);
          } else {
           
            dispatch(getAllLocationGroupsListAction(page, rowsPerPage));
          }
          
          setSnackbar({
            open: true,
            message: 'Location group deleted successfully',
            severity: 'success',
          });
        })
        .catch(() => {
          setSnackbar({
            open: true,
            message: 'Failed to delete location group',
            severity: 'error',
          });
        });
      setOpenDialog(false);
      setSelectedGroupId(null);
    }
  };

  const handleCancelDelete = () => {
    setOpenDialog(false);
    setSelectedGroupId(null);
  };

  const handleAddNewGroup = () => {
    navigate('/locationgroup');
  };

  const handleChangePage = (event, newPage) => {  
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getTruncatedText = (text, maxLength = 15) => {
    if (!text) return "N/A";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };
  
  const totalPages = Math.ceil((total_count.totalElements || 0) / rowsPerPage);

  return (
    <Box sx={{ 
      padding: isMobile ? 2 : 3,
      overflowX: 'auto',
      backgroundColor: '#ffffff',
      minHeight: '100vh'
    }}>
      <Paper sx={{ 
        padding: isMobile ? 2 : 3,
        boxShadow: isMobile ? 1 : 3,
        border: "1px solid rgba(49, 49, 49, 0.1)"
      }}>
       
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          marginBottom: isMobile ? 2 : 3,
          gap: isMobile ? 2 : 0
        }}>        
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            sx={{ 
              fontFamily: '"Poppins", sans-serif',
              fontSize: isMobile ? '18px' : '24px',
              fontWeight: 'bold'
            }}
          >
            Locations Group ({total_count.totalElements || 0})
          </Typography>
          <Button 
            variant="contained" 
            onClick={handleAddNewGroup}
          >
            Add New Group
          </Button>
        </Box>

       
        <TableContainer 
          component={Paper} 
          sx={{ 
            position: 'relative', 
            minHeight: '200px',
            maxHeight: isMobile ? '60vh' : 'none', 
            overflow: 'auto',
            boxShadow: isMobile ? 1 : 2
          }}
        >
          <Table 
            stickyHeader
            sx={{ 
              minWidth: isMobile ? '600px' : '650px',
              '& .MuiTableCell-root': {
                fontSize: isMobile ? '11px' : '14px',
                padding: isMobile ? '6px 8px' : '12px 16px',
                whiteSpace: isMobile ? 'nowrap' : 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: isMobile ? '120px' : '200px'
              }
            }}
            size={isMobile ? "small" : "medium"}
          >
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    position: 'sticky',
                    top: 0,
                    fontSize: isMobile ? '11px' : '14px',
                    padding: isMobile ? '8px' : '16px',
                    minWidth: isMobile ? '120px' : '150px'
                  }}
                >
                  Group Name
                </TableCell>
                <TableCell
                  sx={{ 
                    position: 'sticky',
                    top: 0,
                    fontSize: isMobile ? '11px' : '14px',
                    padding: isMobile ? '8px' : '16px',
                    minWidth: isMobile ? '80px' : '100px'
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  align='right'
                  sx={{ 
                    position: 'sticky',
                    top: 0,
                    fontSize: isMobile ? '11px' : '14px',
                    padding: isMobile ? '8px' : '16px',
                    minWidth: isMobile ? '80px' : '100px'
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
                      <Loader />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : locationGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                      <Typography variant="subtitle1" color="textSecondary">
                        No Data Available
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                locationGroups.map((group,index) => (
                  <StyledTableRow className="styled-table-row" key={group.locationGroupId || `group-${index}`}>
                    <TableCell>
                      {isMobile ? (
                        <Tooltip title={group.name || "N/A"} arrow placement="top">
                          <span>{getTruncatedText(group.name)}</span>
                        </Tooltip>
                      ) : (
                        group.name || "N/A"
                      )}
                    </TableCell>
                    <TableCell>
                      {isMobile ? (
                        <Tooltip title={group.status || "N/A"} arrow placement="top">
                          <span>{getTruncatedText(group.status)}</span>
                        </Tooltip>
                      ) : (
                        group.status || "N/A"
                      )}
                    </TableCell>
                    <TableCell align='right'>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <IconButton 
                          onClick={() => handleEditClick(group)} 
                          aria-label="edit"
                          size={isMobile ? "small" : "medium"}
                          sx={{ p: isMobile ? '4px' : '8px' }}
                        >
                          <EditIcon fontSize={isMobile ? "small" : "medium"} />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDeleteClick(group.id)} 
                          aria-label="delete"
                          size={isMobile ? "small" : "medium"}
                          sx={{ p: isMobile ? '4px' : '8px' }}
                        >
                          <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>

          {!isLoading && locationGroups.length > 0 && (
            <Box sx={{ 
              width: "100%", 
              overflow: "hidden", 
              padding: isMobile ? "12px 0" : "16px 0" 
            }}>
              <Stack
                direction={isMobile ? "column" : "row"}
                justifyContent="flex-end"
                alignItems="center"
                flexWrap="wrap"
                spacing={2}
                sx={{
                  gap: isMobile ? 1 : 2
                }}
              >
               
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" sx={{ 
                    fontSize: isMobile ? "11px" : "14px", 
                    marginRight: "8px" 
                  }}>
                    Rows per page:
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    size={isMobile ? "small" : "medium"}
                    sx={{ 
                      height: isMobile ? '32px' : '36px',
                      fontSize: isMobile ? '11px' : '14px',
                      minWidth: isMobile ? '60px' : '80px',
                      '& .MuiSelect-select': {
                        padding: isMobile ? '8px 32px 8px 12px' : '10px 32px 10px 12px'
                      }
                    }}
                  >
                    {[5, 10, 15, 25, 50].map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                
                {totalPages > 1 && (
                  <Pagination
                    count={totalPages}
                    page={page + 1}
                    onChange={handleChangePage}
                    showFirstButton
                    showLastButton
                    size={isMobile ? "small" : "medium"}
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontSize: isMobile ? '12px' : '14px',
                        minWidth: isMobile ? '28px' : '32px',
                        height: isMobile ? '28px' : '32px'
                      }
                    }}
                  />
                )}
              </Stack>
            </Box>
          )}
        </TableContainer>
 
        <Dialog
          open={openDialog}
          onClose={handleCancelDelete}
          aria-labelledby="confirm-delete-dialog-title"
          aria-describedby="confirm-delete-dialog-description"
          maxWidth="sm"
          fullWidth={isMobile}
          PaperProps={{
            sx: {
              margin: isMobile ? 1 : 3,
              width: isMobile ? 'calc(100% - 16px)' : 'auto'
            }
          }}
        >
          <DialogTitle 
            className="btn-primary"
            sx={{
              height: isMobile ? '56px' : '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '0 12px' : '0 16px',
              fontSize: isMobile ? '16px' : '18px'
            }} 
            id="confirm-delete-dialog-title"
          >
            Confirm Deletion
          </DialogTitle>
          <DialogContent>
            <DialogContentText 
              sx={{
                marginTop: '15px',
                fontSize: isMobile ? '14px' : '16px'
              }} 
              id="confirm-delete-dialog-description"
            >
              Are you sure you want to delete this location group? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ 
            padding: isMobile ? '12px 16px' : '16px 24px',
            gap: 1
          }}>
            <Button 
              onClick={handleCancelDelete} 
              variant='outlined'
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete} 
              autoFocus
              variant='outlined'
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000} 
          onClose={handleSnackbarClose} 
          anchorOrigin={{ 
            vertical: 'top', 
            horizontal: isMobile ? 'center' : 'right'
          }}
          sx={{
            top: isMobile ? '60px' : '24px',
            '& .MuiSnackbarContent-root': {
              fontSize: isMobile ? '12px' : '14px'
            }
          }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ 
              whiteSpace: 'pre-line',
              fontSize: isMobile ? '12px' : '14px',
              width: isMobile ? '90vw' : 'auto',
              maxWidth: isMobile ? '350px' : '500px'
            }}
          >
            {snackbar.message} 
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default LocationGroup;